import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  drawCountdownMoves,
  isDrawCountdownNear,
  isDrawCountdownUrgent,
  type DrawCountdown as DrawCountdownValue,
} from '../../utils/draw-countdown';
import './DrawCountdown.css';

interface DrawCountdownProps {
  value: DrawCountdownValue;
}

/**
 * Quanto manca alla patta automatica, come riga sottile SOPRA la barra di gioco.
 *
 * Prima non c'era niente: chi chiedeva patta in un finale di sole dame e si
 * sentiva dire di no non aveva modo di sapere che la patta stava arrivando lo
 * stesso, e fra quante mosse. Il dato c'era gia' — `halfMoveClock` viaggia
 * intero nel payload — ma non lo vedeva nessuno.
 *
 * DISEGNA DUE REGOLE, non una, e non si accorge della differenza: il limite di
 * mosse (italiana 40, internazionale 25...) e le 7 mosse frisoni di due dame
 * contro una. Non possono essere attive insieme in nessuna delle 27 varianti
 * della matrice, quindi una riga sola basta; `value.kind` serve solo a scegliere
 * la frase che si legge a voce. Vedi `draw-countdown.ts`.
 *
 * E' una riga NEL FLUSSO, e prima non lo era: in `position: absolute` sul fondo
 * della damiera risparmiava i suoi ~14px, ma li risparmiava coprendo il
 * cartellino di chi gioca ogni volta che la damiera era limitata dall'altezza.
 * Ora sta dove sta il pulsante che la riguarda. Vedi DrawCountdown.css.
 *
 * Puramente presentazionale: la memoria di «la patta e' stata chiesta» e la
 * decisione di mostrarsi stanno in DrawCountdownSlot qui sotto.
 */
export function DrawCountdown({ value }: DrawCountdownProps) {
  const { t } = useTranslation();
  const { done, total, remaining } = drawCountdownMoves(value);
  const near = isDrawCountdownNear(value);
  /*
   * Due frasi, perche' le cifre non dicono QUALE patta stia scadendo. La riga si
   * accende anche da sola, senza che nessuno abbia toccato niente: «2/7» a bordo
   * damiera, muto, non spiega perche' sia comparso. Il testo visibile resta le
   * sole cifre (una etichetta a 360px costa larghezza che non c'e'), quindi
   * l'unico posto dove la differenza si puo' dire e' qui.
   */
  const ariaKey =
    value.kind === 'frisian2v1' ? 'game.frisianCountdown.aria' : 'game.drawCountdown.aria';
  // Il riempimento e' in SEMI-mosse: avanza a ogni mossa di dama, mentre il
  // numero conta le mosse complete (che e' come la regola si enuncia).
  const pct = (value.current / value.limit) * 100;

  return (
    <div
      className={`draw-countdown${near ? ' is-near' : ''}`}
      role="status"
      aria-label={t(ariaKey, { count: remaining })}
      data-testid="draw-countdown"
      /* Quale delle due regole: i test hanno bisogno di distinguerle, e
         `data-testid` non si tocca perche' i test di impaginazione ci si
         appoggiano. */
      data-regola={value.kind}
    >
      <span className="draw-countdown__track">
        <span
          className="draw-countdown__fill"
          style={{ width: `${pct}%` }}
          data-testid="draw-countdown-fill"
        />
      </span>
      <span className="draw-countdown__count">{done}/{total}</span>
    </div>
  );
}

interface DrawCountdownSlotProps {
  /**
   * `null` se la variante non ha NESSUNA delle due regole di patta a tempo
   * (Turca, Spagnola, Ceca...), o se il frisone non e' ancora in 2-dame-contro-1:
   * allora non si vede mai, per quanto si chieda patta.
   */
  value: DrawCountdownValue | null;
  drawOffered: boolean;
  opponentOfferedDraw: boolean;
}

/**
 * DUE INNESCHI IN OR, e il secondo e' nato dopo.
 *
 * 1. LA RICHIESTA DI PATTA accende la riga da ZERO e la tiene accesa per il resto
 *    della partita, anche dopo un rifiuto e anche se il contatore si azzera. E'
 *    proprio dopo il rifiuto che serve di piu': chi si e' sentito dire no vuole
 *    sapere che la patta sta arrivando lo stesso.
 *
 * 2. LA SCADENZA VICINA (`isDrawCountdownUrgent`: restano <= 10 mosse) la accende
 *    DA SOLA. Questo prima non c'era, ed era il difetto piu' grosso dei due: la
 *    patta automatica arriva anche se nessuno ha chiesto niente — in italiana
 *    come nel frisone — e chi non chiedeva, non sapeva. Nel frisone poi la
 *    finestra dura 7 mosse e scade da se': legarla a una richiesta vorrebbe dire
 *    non mostrarla quasi mai.
 *
 * Il chiavistello serve ancora, e solo al primo dei due: l'innesco automatico si
 * ricalcola a ogni mossa dal contatore, mentre «la patta e' stata chiesta» e' un
 * fatto accaduto che nessun contatore ricorda.
 *
 * La memoria e' `useState` e non un campo di store perche' il posto in cui vive
 * questo componente la azzera da solo: sta insieme alla barra comandi — in
 * `.gs-toolbar-anchor` sotto i 900px, in `.gs-sidebar-sopra-barra` sopra — e
 * GameScreen smonta entrambi a fine partita, quindi alla rivincita rimonta
 * pulito. Un reset scritto a mano avrebbe dovuto indovinare «e' iniziata una
 * nuova partita», e il candidato ovvio — storico delle mosse vuoto — e' sbagliato
 * con le aperture sorteggiate, che partono con lo storico gia' pieno.
 *
 * Il prezzo di avere due case: attraversare i 900px a partita in corso smonta e
 * rimonta, quindi azzera il chiavistello. E' lo stesso attraversamento che gia'
 * rimonta tutta la barra comandi. Da quando c'e' l'innesco automatico il prezzo
 * e' piu' basso: nel tratto in cui la riga conta davvero si riaccende da se'.
 */
export default function DrawCountdownSlot({
  value,
  drawOffered,
  opponentOfferedDraw,
}: DrawCountdownSlotProps) {
  const [chiesta, setChiesta] = useState(false);

  // SOPRA i return, o al primo `null` l'ordine degli hook salta.
  useEffect(() => {
    if (drawOffered || opponentOfferedDraw) setChiesta(true);
  }, [drawOffered, opponentOfferedDraw]);

  if (!value) return null;
  if (!chiesta && !isDrawCountdownUrgent(value)) return null;
  return <DrawCountdown value={value} />;
}
